import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../hooks/useLanguage';
import { getTranslation } from '../../translations/presentation';

// Animações sofisticadas
const scanline = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  50% { opacity: 1; }
  100% { transform: translateY(100vh); opacity: 0; }
`;

const particleFloat = keyframes`
  0%, 100% { 
    transform: translate(0, 0) scale(1); 
    opacity: 0.2; 
  }
  50% { 
    transform: translate(15px, -15px) scale(1.1); 
    opacity: 0.5; 
  }
`;

const gridPulse = keyframes`
  0%, 100% { opacity: 0.03; }
  50% { opacity: 0.08; }
`;

const glowPulse = keyframes`
  0%, 100% { 
    filter: drop-shadow(0 0 20px rgba(100, 182, 255, 0.4)) 
            drop-shadow(0 0 40px rgba(0, 255, 255, 0.2));
  }
  50% { 
    filter: drop-shadow(0 0 30px rgba(100, 182, 255, 0.6)) 
            drop-shadow(0 0 60px rgba(0, 255, 255, 0.3));
  }
`;

const borderGlow = keyframes`
  0%, 100% { 
    box-shadow: 
      0 0 30px rgba(100, 182, 255, 0.15),
      inset 0 0 30px rgba(100, 182, 255, 0.05),
      0 8px 32px rgba(0, 0, 0, 0.3);
  }
  50% { 
    box-shadow: 
      0 0 50px rgba(100, 182, 255, 0.25),
      inset 0 0 50px rgba(100, 182, 255, 0.08),
      0 8px 32px rgba(0, 0, 0, 0.3);
  }
`;

const dataFlow = keyframes`
  0% { 
    transform: translateX(-100%) scaleX(0.5); 
    opacity: 0; 
  }
  50% { 
    opacity: 1; 
  }
  100% { 
    transform: translateX(100%) scaleX(0.5); 
    opacity: 0; 
  }
`;

const revealStats = keyframes`
  0% { 
    opacity: 0; 
    transform: translateY(40px) scale(0.9);
    filter: blur(15px);
  }
  60% { 
    transform: translateY(-8px) scale(1.02);
  }
  100% { 
    opacity: 1; 
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
`;

const numberGlow = keyframes`
  0%, 100% { 
    filter: drop-shadow(0 0 25px rgba(0, 255, 255, 0.6))
            drop-shadow(0 0 50px rgba(0, 255, 255, 0.4));
  }
  50% { 
    filter: drop-shadow(0 0 35px rgba(0, 255, 255, 0.9))
            drop-shadow(0 0 70px rgba(0, 255, 255, 0.6));
  }
`;

const hologramShift = keyframes`
  0%, 100% { 
    transform: translateX(0px); 
    opacity: 0.15; 
  }
  50% { 
    transform: translateX(2px); 
    opacity: 0.25; 
  }
`;

const cornerExpand = keyframes`
  0%, 100% { 
    width: 30px; 
    height: 30px; 
  }
  50% { 
    width: 40px; 
    height: 40px; 
  }
`;

const energyPulse = keyframes`
  0% { 
    transform: scale(1); 
    opacity: 0.3; 
  }
  50% { 
    transform: scale(1.5); 
    opacity: 0; 
  }
  100% { 
    transform: scale(1); 
    opacity: 0; 
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
  overflow: hidden;
  position: relative;
  cursor: ${props => !props.$statsVisible ? 'pointer' : 'default'};
  background: radial-gradient(ellipse at center, rgba(15, 30, 50, 0.9) 0%, rgba(5, 10, 20, 0.95) 100%);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      linear-gradient(90deg, transparent 0%, rgba(100, 182, 255, 0.02) 50%, transparent 100%),
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(100, 182, 255, 0.02) 2px,
        rgba(100, 182, 255, 0.02) 4px
      );
    animation: ${gridPulse} 5s ease-in-out infinite;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -100%;
    left: 0;
    right: 0;
    height: 100%;
    background: linear-gradient(
      180deg,
      transparent 0%,
      rgba(100, 182, 255, 0.08) 50%,
      transparent 100%
    );
    animation: ${scanline} 10s linear infinite;
    pointer-events: none;
  }
`;

const TechGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(100, 182, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(100, 182, 255, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  opacity: 0.4;
`;

const Particles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const Particle = styled.div`
  position: absolute;
  width: 2px;
  height: 2px;
  background: rgba(100, 182, 255, 0.7);
  border-radius: 50%;
  animation: ${particleFloat} ${props => props.$duration}s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  top: ${props => props.$top}%;
  left: ${props => props.$left}%;
  box-shadow: 0 0 8px rgba(100, 182, 255, 0.6), 0 0 15px rgba(0, 255, 255, 0.3);
`;

const ContentPreview = styled.div`
  max-width: 1400px;
  width: 95%;
  margin: 0 auto;
  padding: 120px 100px;
  border: 1px solid rgba(100, 182, 255, 0.25);
  border-radius: 24px;
  background: rgba(5, 15, 30, 0.6);
  backdrop-filter: blur(30px);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: ${borderGlow} 4s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent 0%,
      rgba(100, 182, 255, 0.1) 20%,
      rgba(0, 255, 255, 0.6) 50%,
      rgba(100, 182, 255, 0.1) 80%,
      transparent 100%
    );
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent 0%,
      rgba(100, 182, 255, 0.1) 20%,
      rgba(0, 255, 255, 0.6) 50%,
      rgba(100, 182, 255, 0.1) 80%,
      transparent 100%
    );
  }
  
  @media (max-width: 768px) {
    padding: 60px 40px;
  }
`;

const DataFlowLine = styled.div`
  position: absolute;
  height: 1px;
  width: 100%;
  background: linear-gradient(90deg, 
    transparent 0%,
    rgba(0, 255, 255, 0.4) 30%,
    rgba(100, 182, 255, 0.8) 50%,
    rgba(0, 255, 255, 0.4) 70%,
    transparent 100%
  );
  animation: ${dataFlow} 4s ease-in-out infinite;
  opacity: 0.5;
  filter: blur(0.5px);
  
  &:nth-child(1) { top: 15%; animation-delay: 0s; }
  &:nth-child(2) { top: 50%; animation-delay: 1.5s; }
  &:nth-child(3) { top: 85%; animation-delay: 3s; }
`;

const MainTitle = styled.h1`
  font-family: 'Orbitron', sans-serif;
  font-size: 6rem;
  font-weight: 700;
  color: #64B6FF;
  text-align: center;
  margin: 0;
  letter-spacing: 20px;
  text-transform: uppercase;
  animation: ${glowPulse} 4s ease-in-out infinite;
  line-height: 1.3;
  position: relative;
  z-index: 2;
  background: linear-gradient(135deg, 
    rgba(140, 202, 255, 0.95) 0%, 
    rgba(92, 227, 255, 0.98) 25%,
    rgba(70, 255, 255, 1) 50%,
    rgba(92, 227, 255, 0.98) 75%,
    rgba(140, 202, 255, 0.95) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    letter-spacing: 8px;
  }
`;

const TitleLine = styled.div`
  display: block;
  margin: 15px 0;
`;

const Subtitle = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.8rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.75);
  text-align: center;
  margin: 40px 0 0 0;
  letter-spacing: 8px;
  text-transform: uppercase;
  position: relative;
  z-index: 2;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 60px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(100, 182, 255, 0.5));
  }
  
  &::before {
    right: calc(100% + 30px);
  }
  
  &::after {
    left: calc(100% + 30px);
    background: linear-gradient(90deg, rgba(100, 182, 255, 0.5), transparent);
  }
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    letter-spacing: 3px;
    margin: 25px 0 0 0;
    
    &::before,
    &::after {
      display: none;
    }
  }
`;

const SubtitleText = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 2rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
  margin: 60px 0 0 0;
  letter-spacing: 2px;
  position: relative;
  z-index: 2;
  animation: ${revealStats} 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  opacity: 0;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin: 35px 0 0 0;
  }
`;

const Highlight = styled.span`
  color: #00FFFF;
  font-weight: 600;
  font-size: 3.5rem;
  animation: ${numberGlow} 3s ease-in-out infinite;
  display: inline-block;
  letter-spacing: 3px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ClickHint = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  font-family: 'Orbitron', sans-serif;
  font-size: 0.85rem;
  font-weight: 300;
  color: rgba(100, 182, 255, 0.4);
  letter-spacing: 3px;
  text-transform: uppercase;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.8s ease;
  pointer-events: none;
  
  @media (max-width: 768px) {
    bottom: 20px;
    font-size: 0.65rem;
  }
`;

const TechCorner = styled.div`
  position: absolute;
  width: 40px;
  height: 40px;
  pointer-events: none;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    background: linear-gradient(135deg, rgba(100, 182, 255, 0.6), rgba(0, 255, 255, 0.4));
  }
  
  &::before {
    width: 100%;
    height: 2px;
  }
  
  &::after {
    width: 2px;
    height: 100%;
  }
  
  &.top-left {
    top: 0;
    left: 0;
    animation: ${cornerExpand} 3s ease-in-out infinite;
    
    &::before { top: 0; left: 0; }
    &::after { top: 0; left: 0; }
  }
  
  &.top-right {
    top: 0;
    right: 0;
    animation: ${cornerExpand} 3s ease-in-out infinite 0.75s;
    
    &::before { top: 0; right: 0; }
    &::after { top: 0; right: 0; }
  }
  
  &.bottom-left {
    bottom: 0;
    left: 0;
    animation: ${cornerExpand} 3s ease-in-out infinite 1.5s;
    
    &::before { bottom: 0; left: 0; }
    &::after { bottom: 0; left: 0; }
  }
  
  &.bottom-right {
    bottom: 0;
    right: 0;
    animation: ${cornerExpand} 3s ease-in-out infinite 2.25s;
    
    &::before { bottom: 0; right: 0; }
    &::after { bottom: 0; right: 0; }
  }
`;

const HologramEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 1px solid rgba(100, 182, 255, 0.15);
    border-radius: 24px;
    animation: ${hologramShift} 2s ease-in-out infinite;
  }
  
  &::after {
    animation-delay: 1s;
  }
`;

const EnergyOrb = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(100, 182, 255, 0.15) 0%, transparent 70%);
  animation: ${energyPulse} 4s ease-in-out infinite;
  pointer-events: none;
  
  &.orb-1 {
    top: -150px;
    left: -150px;
  }
  
  &.orb-2 {
    bottom: -150px;
    right: -150px;
    animation-delay: 2s;
  }
  
  @media (max-width: 768px) {
    width: 200px;
    height: 200px;
    
    &.orb-1 {
      top: -100px;
      left: -100px;
    }
    
    &.orb-2 {
      bottom: -100px;
      right: -100px;
    }
  }
`;

const PresentationSlide10 = () => {
  const { lang } = useLanguage();
  const t = getTranslation('slide10', lang);
  const [statsVisible, setStatsVisible] = useState(false);

  const handleClick = () => {
    if (!statsVisible) {
      setStatsVisible(true);
    }
  };

  // Gera partículas aleatórias
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    top: Math.random() * 100,
    left: Math.random() * 100,
    duration: 3 + Math.random() * 4,
    delay: Math.random() * 2
  }));

  return (
    <SlideWrapper onClick={handleClick} $statsVisible={statsVisible}>
      <TechGrid />
      
      <Particles>
        {particles.map(particle => (
          <Particle
            key={particle.id}
            $top={particle.top}
            $left={particle.left}
            $duration={particle.duration}
            $delay={particle.delay}
          />
        ))}
      </Particles>
      
      <ContentPreview>
        <TechCorner className="top-left" />
        <TechCorner className="top-right" />
        <TechCorner className="bottom-left" />
        <TechCorner className="bottom-right" />
        
        <HologramEffect />
        
        <EnergyOrb className="orb-1" />
        <EnergyOrb className="orb-2" />
        
        <DataFlowLine />
        <DataFlowLine />
        <DataFlowLine />
        
        <MainTitle>
          Journey Agency
        </MainTitle>
        
        <Subtitle>
          {t.subtitle}
        </Subtitle>
        
        {statsVisible && (
          <SubtitleText>
            <Highlight>+10.000</Highlight> {t.appointments}
          </SubtitleText>
        )}
      </ContentPreview>
    </SlideWrapper>
  );
};

export default PresentationSlide10;
